import type { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { useAuth } from 'context/AuthContext';
import { useBikeFilter } from 'context/BikeFilterContext';
import type { UserBike } from 'models/shared';
import { useState } from 'react';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import { ChevronRightIcon, PlusIcon } from '@c/icons';
import Link from 'next/link';
import MobileSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/MobileSearchByVehicle';
import { cn, isMobile } from '@util/index';
import DesktopSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/DesktopSearchByVehicle';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { logError } from '@util/logError';
import { addUserBike } from '@util/firestore/users';
import { useQueryClient } from '@tanstack/react-query';
import { prefix } from '@util/imagePrefix';
import { usePathname } from 'next/navigation';
import { ShopByDirtBike } from '@ui/ShopByVehicle';
import SafeImage from '@ui/SafeImage';
import InfoIcon from '@c/icons/info';
import Select from '@ui/Select';

export const FindYourVehicle = ({
  onBikeComplete,
  sideFilters,
  hideYourSavedBikes,
}: {
  onBikeComplete: (bike: UserBike) => void;
  sideFilters: CategoryPageSideFilters;
  hideYourSavedBikes?: boolean;
}) => {
  const { userDoc } = useAuth();
  const { selectedBike } = useBikeFilter();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [modalMode, setModalMode] = useState<'filter' | 'add-to-garage'>(
    'filter'
  );
  const queryClient = useQueryClient();
  const pathname = usePathname();
  const [showDesktopSearchByVehicle, setShowDesktopSearchByVehicle] =
    useState(false);

  async function addBikeToGarage(bike: Partial<UserBike>) {
    if (!userDoc) return;

    try {
      if (
        (userDoc.bikes ?? []).findIndex(
          (b) =>
            b.model === bike.model &&
            b.year === bike.year &&
            b.make === bike.make
        ) === -1
      ) {
        await addUserBike(userDoc.uid, {
          ...(bike as UserBike), // passing bike as UserBike but is is actually a Partial<UserBike>
          date_added: Date.now(),
        });

        onBikeComplete({
          ...(bike as UserBike),
        });
      }
    } catch (e) {
      logError(e);
    }
  }

  return (
    <div className="w-full bg-[#F6F6F6] px-[2.4rem] py-[2.4rem] sm:px-0 sm:py-[1.6rem]">
      {/* mobile-only header — title + black pill button */}
      <div className="flex w-full items-center justify-between sm:hidden">
        <div className="flex flex-col">
          <p className="text-[1.8rem] font-bold leading-[1.5] text-brand-black">
            Find your ride
          </p>
          <p className="text-[1.6rem] font-medium leading-[1.4] text-[#444]">
            Filter by year, make & model
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setModalMode('filter');
            setShowMobileSearchByVehicle(true);
          }}
          className="flex h-[4rem] shrink-0 items-center justify-center rounded-full bg-brand-black px-[1.8rem] text-[1.6rem] font-semibold text-white"
        >
          Search by vehicle
        </button>
      </div>

      <div className="hidden w-full flex-col justify-between gap-y-12 sm:mx-auto sm:flex sm:max-w-content sm:flex-row sm:gap-y-0">
        <div className="flex w-full flex-col gap-[1.6rem] sm:flex-row sm:items-center">
          <div className="hidden flex-col gap-1 whitespace-nowrap font-semibold sm:flex">
            <span className="text-[2rem]">Find Vehicle</span>

            {!userDoc && (
              <Link
                href={`/login?redirect=${encodeURIComponent(pathname ?? '')}`}
                className="flex items-center gap-2 whitespace-nowrap text-[1.5rem] text-brand-secondary hover:underline"
              >
                Log In to Add Your Ride
                <ChevronRightIcon className="h-8 w-8" />
              </Link>
            )}
          </div>

          <VehicleSelector
            fill={false}
            setShowMobileSearchByVehicle={setShowMobileSearchByVehicle}
            setShowDesktopSearchByVehicle={setShowDesktopSearchByVehicle}
            setModalMode={setModalMode}
          />
        </div>

        {!!userDoc?.bikes?.length && !hideYourSavedBikes && (
          <div className="flex items-center gap-x-2">
            <div className="flex items-center justify-center gap-[1.6rem] sm:min-w-[30rem]">
              <Select
                allowEmpty={false}
                key={`${sideFilters.bikeYears?.[0]} ${
                  sideFilters.bikeMakes?.[0] ?? ''
                } ${sideFilters.bikeModels?.[0] ?? ''}`}
                placeholder="Your saved rides"
                grow
                options={
                  userDoc?.bikes?.length
                    ? userDoc?.bikes.map((bike) => ({
                        label: `${bike.year} ${bike.make} ${bike.model}`,
                        value: bike,
                        id: `${bike.year}-${bike.make}-${bike.model}-${bike.type}`,
                      }))
                    : [
                        {
                          label: 'No bikes saved for this ride type',
                          value: 'No bikes saved for this ride type',
                          id: 'No bikes saved for this ride type',
                        },
                      ]
                }
                value={`${sideFilters.bikeYears?.[0] ?? ''}-${
                  sideFilters.bikeMakes?.[0] ?? ''
                }-${sideFilters.bikeModels?.[0] ?? ''}-${
                  sideFilters.rideTypes?.[0] ?? ''
                }`}
                onChange={(e) => {
                  const bike = e.value;
                  onBikeComplete(bike);

                  if (bike?.type) {
                    sideFilters.rideTypes = [bike.type];
                  }
                }}
              />
            </div>

            <button
              type="button"
              className="duration-125 flex h-[4rem] w-[4rem] cursor-pointer items-center justify-center rounded-2xl bg-brand-secondary transition-all hover:bg-brand-secondary-darker"
              onClick={() => {
                setModalMode('add-to-garage');

                if (isMobile()) {
                  setShowMobileSearchByVehicle(true);
                } else {
                  setShowDesktopSearchByVehicle(true);
                }
              }}
            >
              <div className="flex h-[2rem] w-[2rem] items-center justify-center rounded-full bg-white">
                <PlusIcon className="text-brand-secondary" />
              </div>
            </button>
          </div>
        )}

        <MobileSearchByVehicle
          show={showMobileSearchByVehicle}
          dismiss={() => setShowMobileSearchByVehicle(false)}
          onBikeComplete={
            modalMode === 'filter' ? onBikeComplete : addBikeToGarage
          }
          variant={modalMode}
          initialBike={modalMode === 'filter' ? selectedBike : null}
          userDoc={userDoc}
        />

        <DesktopSearchByVehicle
          isOpen={showDesktopSearchByVehicle}
          dismiss={() => setShowDesktopSearchByVehicle(false)}
          onBikeComplete={
            modalMode === 'filter' ? onBikeComplete : addBikeToGarage
          }
          variant={modalMode}
          initialBike={modalMode === 'filter' ? selectedBike : null}
        />
      </div>
    </div>
  );
};

export const VehicleSelector = ({
  fill = true,
  setShowMobileSearchByVehicle,
  setShowDesktopSearchByVehicle,
  setModalMode,
}: {
  fill?: boolean;
  setShowMobileSearchByVehicle: (show: boolean) => void;
  setShowDesktopSearchByVehicle: (show: boolean) => void;
  setModalMode?: (mode: 'add-to-garage' | 'filter') => void;
}) => {
  const { selectedBike } = useBikeFilter();
  const { userDoc } = useAuth();
  const pathname = usePathname();

  const hasSelectedBike =
    selectedBike.year && selectedBike.make && selectedBike.model;

  return (
    <div className="flex h-full w-full items-center justify-end gap-x-8">
      <div className="hidden h-[4.5rem] w-[1.2px] bg-[#D9D9D9] sm:flex" />

      <button
        type="button"
        className={cn(
          'flex w-[20rem] items-center justify-between rounded-[1.5rem] px-[2.4rem] py-[1rem] text-[1.6rem] font-medium',
          fill ? 'w-full' : 'mr-2 w-[30rem]',
          userDoc || pathname?.includes('product')
            ? 'border border-zinc-100 bg-zinc-200/60 text-zinc-800 transition-colors duration-75 ease-linear hover:bg-zinc-200'
            : 'bg-brand-secondary text-white'
        )}
        onClick={() => {
          if (isMobile()) {
            setModalMode?.('filter');
            setShowMobileSearchByVehicle(true);
          } else {
            setModalMode?.('filter');
            setShowDesktopSearchByVehicle(true);
          }
        }}
      >
        <span className="truncate">
          {hasSelectedBike
            ? `${selectedBike.year} ${selectedBike.make} ${selectedBike.model}`
            : 'Search by vehicle'}
        </span>

        <MagnifyingGlassIcon className="h-8 w-8" />
      </button>
    </div>
  );
};

export const ShopByVehicle = ({
  sideFilters,
  onBikeComplete,
  setShowMobileEditGarageModal,
}: {
  sideFilters: PageSideFilters;
  onBikeComplete: (bike: UserBike | null) => void;
  setShowMobileEditGarageModal?: (show: boolean) => void;
}) => {
  const { userDoc } = useAuth();
  const { selectedBike } = useBikeFilter();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);

  const hasSelectedBike =
    selectedBike.year && selectedBike.make && selectedBike.model;

  const parsedBrandName = selectedBike?.make
    ?.split(' ')
    .join('-')
    .toLowerCase();

  const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

  const shouldShowSelectVehicle =
    !userDoc || (userDoc.bikes?.length === 0 && !hasSelectedBike);

  return (
    <>
      <div className="hidden w-full flex-col gap-[1.6rem] bg-[#edededfe] px-4 py-8 sm:flex sm:flex-row sm:items-center sm:rounded-[1.2rem] sm:px-8">
        <ShopByDirtBike
          sideFilters={sideFilters}
          onBikeComplete={onBikeComplete}
        />
      </div>

      {/* mobile view of shop by vehicle */}
      <div className="mb-2 w-full sm:mb-8 sm:hidden">
        <div className="flex w-full flex-col items-start justify-between space-y-10 bg-[#F6F6F6] p-[2rem]">
          <div className="flex w-full items-center justify-between">
            <div className="flex flex-col items-start gap-2">
              <span className="text-[2.2rem] font-semibold text-black">
                Find parts that fit
              </span>

              {!userDoc && (
                <p className="text-[1.6rem] font-medium text-zinc-700">
                  Quickly find parts that fit your vehicle.
                </p>
              )}
            </div>

            {userDoc && !!userDoc.bikes?.length && (
              <button
                type="button"
                className="text-[1.8rem] font-semibold text-brand-secondary"
                onClick={() => {
                  setShowMobileEditGarageModal?.(true);
                }}
              >
                Edit
              </button>
            )}
          </div>

          {!hasSelectedBike ? (
            <>
              {shouldShowSelectVehicle && (
                <button
                  type="button"
                  className="h-[7rem] w-full rounded-[2rem] bg-brand-secondary text-[2.1rem] font-semibold text-white"
                  onClick={() => {
                    setShowMobileSearchByVehicle(true);
                  }}
                >
                  Select a vehicle
                </button>
              )}
            </>
          ) : (
            <>
              {!userDoc && (
                <div className="flex w-full flex-col space-y-12">
                  <div className="flex h-fit w-full items-center justify-between rounded-[2rem] border border-brand-secondary bg-white px-8 py-2 shadow-sm">
                    <div className="flex items-center gap-6">
                      <SafeImage
                        src={brandImage}
                        alt={selectedBike.make ?? ''}
                        className="h-auto w-32"
                        fallback={
                          <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[0.5rem] bg-zinc-200 p-1 text-center text-[1rem] font-medium text-zinc-600">
                            {selectedBike.make}
                          </div>
                        }
                      />

                      <div className="flex flex-col">
                        <span className="text-[1.8rem] font-medium text-black">
                          {selectedBike.make}
                        </span>

                        <span className="w-[25rem] truncate text-[1.6rem] font-medium text-zinc-700">
                          {selectedBike.year} {selectedBike.model}
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="text-[1.8rem] font-semibold text-brand-secondary"
                      onClick={() => {
                        setShowMobileSearchByVehicle(true);
                      }}
                    >
                      Change
                    </button>
                  </div>

                  <div className="flex items-center gap-2">
                    <InfoIcon className="h-10 w-10 text-brand-secondary" />

                    <span className="text-[1.6rem] font-medium text-zinc-800">
                      <Link href="/signup" className="font-bold underline">
                        Sign up
                      </Link>{' '}
                      to save your vehicle info
                    </span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        onBikeComplete={onBikeComplete}
        initialBike={hasSelectedBike ? selectedBike : null}
        userDoc={userDoc}
      />
    </>
  );
};
